import { BoxProgramVisibility } from "@/modules/boxProgrammation/boxProgramTypes"
import {
  DEFAULT_BOX_PROGRAMMATION_COLOR,
  getBoxProgramColorTypeWithHex,
} from "@/modules/boxProgrammation/boxProgramUtils"
import { type BoxProgramColorType } from "@/modules/boxProgrammation/components/boxProgramColors"
import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import type { Language } from "@/modules/language/utils/languageUtils"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@modules/boxSlot/boxSlotTypes"
import { word } from "@modules/core/utils/i18n"
import { type UserEntity } from "@modules/profile/userProfile/userEntity"
import { z } from "zod"

export type BoxProgramFormFields = {
  image: MediaItemFormType | null
  name: string
  description: string
  color: BoxProgramColorType
  offerIds: string[]
  visibility: BoxProgramVisibility
  defaultHideSessionContent: boolean
}

export const getBoxProgramSchema = (imageRequired: boolean, _language: Language) =>
  z.object({
    image: imageRequired
      ? z.custom<MediaItemFormType | null>((val) => {
          if (!val) return false
          return (val as MediaItemFormType).key !== null
        }, word("program.form.uploadImage.requiredText"))
      : z.custom<MediaItemFormType | null>(),
    name: z
      .string()
      .trim()
      .min(1, word("global.form.required"))
      .max(64, word("form.maximum.characters", { count: 64 })),
    description: z.string().trim().min(1, word("global.form.required")),
    color: z.custom<BoxProgramColorType>(),
    offerIds: z.array(z.string()),
    visibility: z.enum(BoxProgramVisibility),
    defaultHideSessionContent: z.boolean(),
  })

export type BoxProgramDefaultSettingsFormFields = {
  duration: number
  participantLimit: number | null
  location: string | null
  limitStart?: number
  limitEnd?: number
  limitCancel?: number
  allowMultiBooking: boolean
  assignedStaffMembers: UserEntity[]
  dropinsAccepted: boolean
  trialsAccepted: boolean
}

export const getDefaultSettingsSchema = (_language: Language) =>
  z
    .object({
      duration: z.number().min(1, word("global.form.minimumValue", { minValue: 1 })),
      participantLimit: z
        .number({ message: word("global.form.required") })
        .min(
          BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
          word("global.form.minimumValue", { minValue: BOX_SLOT_PARTICIPANTS_LIMIT_MIN }),
        )
        .max(
          BOX_SLOT_PARTICIPANTS_LIMIT_MAX,
          word("global.form.maximumValue", { maxValue: BOX_SLOT_PARTICIPANTS_LIMIT_MAX }),
        ),
      location: z
        .string()
        .trim()
        .max(40, word("form.maximum.characters", { count: 40 }))
        .nullable(),
      limitStart: z
        .number({ message: word("global.form.required") })
        .min(1, word("global.form.minimumValue", { minValue: 1 })),
      limitEnd: z
        .number({ message: word("global.form.required") })
        .min(1, word("global.form.minimumValue", { minValue: 1 })),
      limitCancel: z
        .number({ message: word("global.form.required") })
        .min(1, word("global.form.minimumValue", { minValue: 1 })),
      allowMultiBooking: z.boolean(),
      assignedStaffMembers: z.array(z.custom<UserEntity>()),
      dropinsAccepted: z.boolean(),
      trialsAccepted: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.limitStart <= data.limitEnd) {
        ctx.addIssue({
          code: "custom",
          message: word("box.slotTemplateGroup.form.registration.end_error"),
          path: ["limitEnd"],
        })
      }
    })

export const getBoxProgramDraftValues = (offerIds: string[]): BoxProgramFormFields => ({
  name: "",
  description: "",
  image: null,
  color: getBoxProgramColorTypeWithHex(DEFAULT_BOX_PROGRAMMATION_COLOR),
  visibility: BoxProgramVisibility.MEMBERS,
  defaultHideSessionContent: false,
  offerIds: offerIds,
})

export const getDefaultSettingsDraftValues = (): BoxProgramDefaultSettingsFormFields => ({
  duration: 60,
  participantLimit: null,
  location: null,
  limitStart: undefined,
  limitEnd: undefined,
  limitCancel: undefined,
  allowMultiBooking: false,
  assignedStaffMembers: [],
  dropinsAccepted: true,
  trialsAccepted: true,
})
